/**
 * Short invite link entry (PF-5075).
 *
 * Resolves `/o/{shortId}-{utm}` tokens to the underlying `signedUpPatients` id and forwards into the
 * regular onboarding flow at `/webAppOnboarding?s={id}&u={utm}`. On an unknown/expired code it forwards
 * to `/webAppOnboarding` without a sign-up id, where the existing sign-up gate renders the invalid-invite
 * screen. The base62 `shortId` never contains `-`, so the first `-` separates code from UTM suffix.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchSignedUpPatientInviteContextByShortId } from '../api/fetchSignedUpPatientInviteContext';
import { OnboardingFlowLoadingScreen } from '../components/layout/OnboardingFlowLoadingScreen';

/** Splits `{shortId}-{utm}` at the first `-`; returns `[shortId, utm?]`. */
function splitInviteToken(token: string): [string, string | undefined] {
  const i = token.indexOf('-');
  return i === -1 ? [token, undefined] : [token.slice(0, i), token.slice(i + 1)];
}

export function ShortInviteLinkEntry() {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const [shortId, utmCode] = splitInviteToken(token.trim());
    if (!shortId) {
      navigate('/webAppOnboarding', { replace: true });
      return;
    }

    let active = true;
    const forwardToInvalid = () => {
      if (active) navigate('/webAppOnboarding', { replace: true });
    };

    void fetchSignedUpPatientInviteContextByShortId(shortId, utmCode)
      .then((r) => {
        if (!active) return;
        if (r.status === 'success' && !r.notFound && r.data?.signedUpPatientId) {
          const utm = utmCode ? `&u=${encodeURIComponent(utmCode)}` : '';
          navigate(`/webAppOnboarding?s=${encodeURIComponent(r.data.signedUpPatientId)}${utm}`, { replace: true });
        } else {
          forwardToInvalid();
        }
      })
      .catch(forwardToInvalid);

    return () => {
      active = false;
    };
  }, [token, navigate]);

  return <OnboardingFlowLoadingScreen />;
}
